
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface WordConfidence {
  word: string;
  confidence: number;
}

export interface TranscriptionConfidence {
  text: string;
  overallConfidence: number;
  words: WordConfidence[];
  needsReprocessing: boolean;
  isHighQuality: boolean;
}

interface ConfidenceDisplayProps {
  confidence: TranscriptionConfidence;
  onRetranscribe?: () => void;
  showWordLevel?: boolean;
}

const ConfidenceDisplay: React.FC<ConfidenceDisplayProps> = ({
  confidence,
  onRetranscribe,
  showWordLevel = false
}) => {
  const getConfidenceColor = (level: number) => {
    if (level >= 0.8) return 'text-green-600';
    if (level >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (level: number) => {
    if (level >= 0.8) return 'Alta';
    if (level >= 0.6) return 'Média';
    return 'Baixa';
  };

  const getConfidenceBadgeVariant = (level: number) => {
    if (level >= 0.8) return 'default' as const;
    if (level >= 0.6) return 'secondary' as const;
    return 'destructive' as const;
  };

  return (
    <div className="space-y-3 p-3 bg-white/50 backdrop-blur-sm rounded-lg border">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-2">
          {confidence.overallConfidence >= 0.8 ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
          )}
          Confiança da Transcrição
        </h4>
        <Badge variant={getConfidenceBadgeVariant(confidence.overallConfidence)}>
          {getConfidenceLabel(confidence.overallConfidence)}
        </Badge>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span>Confiança Geral</span>
          <span className={getConfidenceColor(confidence.overallConfidence)}>
            {Math.round(confidence.overallConfidence * 100)}%
          </span>
        </div>
        <Progress 
          value={confidence.overallConfidence * 100} 
          className="h-2"
        />
      </div>

      {confidence.needsReprocessing && onRetranscribe && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <div className="flex-1">
            <p className="text-xs text-yellow-800">
              Baixa confiança detectada. Reprocessar pode melhorar a precisão.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRetranscribe}
            className="text-xs"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Reprocessar
          </Button>
        </div>
      )}

      {showWordLevel && confidence.words.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-xs font-medium text-gray-600">
            Confiança por Palavra
          </h5>
          <div className="flex flex-wrap gap-1">
            {confidence.words.map((word, index) => (
              <span
                key={index}
                className={`px-2 py-1 text-xs rounded ${
                  word.confidence >= 0.8
                    ? 'bg-green-100 text-green-800'
                    : word.confidence >= 0.6
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}
                title={`Confiança: ${Math.round(word.confidence * 100)}%`}
              >
                {word.word}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500">
        <p>Texto: "{confidence.text.slice(0, 50)}{confidence.text.length > 50 ? '...' : ''}"</p>
      </div>
    </div>
  );
};

export default ConfidenceDisplay;
